import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  PlusCircle, ListChecks, CheckCircle2, BarChart3, Settings, LogOut, Zap, MessageCircle, CalendarClock, IdCard,
} from "lucide-react";

const navItems = [
  { key: "new_glitch", to: "/", label: "New Glitch", icon: PlusCircle, end: true },
  { key: "status", to: "/status", label: "Glitch Status", icon: ListChecks },
  { key: "closed", to: "/closed", label: "Closed Today", icon: CheckCircle2 },
  { key: "guest_log", to: "/guest-log", label: "Guest Log", icon: MessageCircle },
  { key: "board", to: "/board", label: "What's On Today", icon: CalendarClock },
  // Its own permission, independent of "board" — an admin can turn What's
  // On Today and Guest Card on/off separately for a role.
  { key: "guest_card", to: "/guest-card", label: "Guest Card", icon: IdCard },
  { key: "reports", to: "/reports", label: "Reports", icon: BarChart3 },
];

export default function Layout({ children, hasBanner }) {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const appName = settings?.general?.app_name || "Glitch Manager";

  const isAdmin = user?.role === "admin";
  const allowedPages = isAdmin ? navItems.map((n) => n.key) : (user?.permissions?.pages || []);
  const items = navItems.filter((n) => allowedPages.includes(n.key));
  // Every module in the catalog is enabled for this account — the icon row
  // is already at its widest, so the brand mark is what gets dropped to
  // keep everything fitting on one line instead of wrapping or crowding.
  const allModulesEnabled = items.length >= navItems.length;
  const hasAdminAccess = isAdmin || (user?.permissions?.admin_sections || []).length > 0;
  if (hasAdminAccess) items.push({ to: "/admin", label: "Admin", icon: Settings });

  const iconOnlyLinkClass = ({ isActive }) =>
    `flex items-center justify-center p-2.5 rounded-lg transition-all ${
      isActive
        ? "bg-[#4e73df] text-white shadow-sm"
        : "text-slate-600 hover:bg-[#4e73df]/10 hover:text-[#4e73df]"
    }`;

  return (
    <div className="min-h-screen" style={{ paddingTop: hasBanner ? 52 : 0 }}>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 gm-shadow">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          {!allModulesEnabled && (
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2.5 shrink-0"
              data-testid="brand-logo"
            >
              <div className="w-9 h-9 rounded-xl bg-[#eef1fb] border border-[#dfe4fb] flex items-center justify-center text-[#5b6ef5]">
                <Zap className="w-5 h-5 fill-[#5b6ef5]" />
              </div>
              <div className="text-left leading-none hidden sm:block">
                <span className="block font-bold text-[#3a3b45] text-[15px]">{appName}</span>
                <span className="block text-[10px] text-slate-400 font-medium tracking-wide">
                  INCIDENT MANAGEMENT
                </span>
              </div>
            </button>
          )}

          <nav className="hidden lg:flex items-center gap-1">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} className={iconOnlyLinkClass} title={it.label} aria-label={it.label} data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
                <it.icon className="w-4 h-4" />
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div className="text-right leading-none">
              <span className="block text-sm font-semibold text-slate-700">{user?.username}</span>
              <span className="block text-[11px] text-slate-400">{user?.department || user?.role}</span>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>

          {/* Mobile: icon-only nav in the top bar */}
          <nav className="flex lg:hidden items-center gap-0.5" data-testid="mobile-icon-nav">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                title={it.label}
                aria-label={it.label}
                data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `p-2.5 rounded-lg transition-colors ${isActive ? "bg-[#4e73df] text-white" : "text-slate-500 hover:bg-[#4e73df]/10 hover:text-[#4e73df]"}`
                }
              >
                <it.icon className="w-5 h-5" />
              </NavLink>
            ))}
            <button
              onClick={logout}
              title="Logout"
              aria-label="Logout"
              data-testid="mobile-logout-btn"
              className="p-2.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">{children}</main>

      <footer className="border-t border-slate-200 mt-8 py-5 text-center text-xs text-slate-400">
        Built for luxury hospitality operations
      </footer>
    </div>
  );
}
